import React, { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Send, Heart, MoreVertical, MessageCircle } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/use-toast';
import { CommunityPostComment, CreateCommentData } from '@/types/community';
import { fetchPostComments, createComment, deleteComment } from '@/services/communityPostService';
import { useRole } from '@/hooks/useRole';
import { useUserProfile } from '@/hooks/useUserProfile';

interface PostCommentsProps {
  postId: string;
  onCommentCreated?: () => void; // Callback para atualizar ranking
}

const PostComments: React.FC<PostCommentsProps> = ({ postId, onCommentCreated }) => {
  const { authUser } = useRole();
  const { profile } = useUserProfile();
  const { toast } = useToast();
  const [comments, setComments] = useState<CommunityPostComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);

  // Carregar comentários
  const loadComments = async () => {
    try {
      setIsLoading(true);
      const response = await fetchPostComments({ post_id: postId });
      console.log('📝 [PostComments] Comentários carregados:', response.comments);
      
      // Debug: verificar se as imagens estão sendo carregadas
      response.comments.forEach((comment, index) => {
        console.log(`📝 [PostComments] Comentário ${index + 1}:`, {
          id: comment.id,
          author: comment.author?.nickname,
          avatar_url: comment.author?.avatar_url,
          hasImage: !!comment.author?.avatar_url
        });
      });
      
      setComments(response.comments);
    } catch (error) {
      console.error('❌ [PostComments] Erro ao carregar comentários:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os comentários",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Criar comentário
  const handleCreateComment = async () => {
    if (!newComment.trim()) {
      toast({
        title: "Erro",
        description: "O comentário não pode estar vazio",
        variant: "destructive",
      });
      return;
    }

    if (!authUser) {
      toast({
        title: "Erro",
        description: "Você precisa estar logado para comentar",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsCreating(true);
      
      const commentData: CreateCommentData = {
        post_id: postId,
        content: newComment.trim()
      };

      console.log('📝 [PostComments] Criando comentário:', commentData);
      
      const newCommentResponse = await createComment(commentData);
      console.log('✅ [PostComments] Comentário criado:', newCommentResponse);
      
      // Recarregar todos os comentários para garantir que as imagens estejam corretas
      await loadComments();
      setNewComment('');
      
      toast({
        title: "Sucesso",
        description: "Comentário publicado!",
      });

      // Notificar componente pai sobre novo comentário
      if (onCommentCreated) {
        onCommentCreated();
      }
      
    } catch (error) {
      console.error('❌ [PostComments] Erro ao criar comentário:', error);
      toast({
        title: "Erro",
        description: "Não foi possível publicar o comentário",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Deletar comentário
  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('Tem certeza que deseja deletar este comentário?')) {
      return;
    }

    try {
      setDeletingCommentId(commentId);
      
      await deleteComment(commentId, postId);
      setComments(prev => prev.filter(c => c.id !== commentId));
      
      toast({
        title: "Sucesso",
        description: "Comentário deletado!",
      });
    } catch (error) {
      console.error('❌ [PostComments] Erro ao deletar comentário:', error);
      toast({
        title: "Erro",
        description: "Não foi possível deletar o comentário",
        variant: "destructive",
      });
    } finally {
      setDeletingCommentId(null);
    }
  };

  useEffect(() => {
    loadComments();
  }, [postId]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-[#2A3749] rounded-full"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-[#2A3749] rounded w-1/4"></div>
              <div className="h-3 bg-[#2A3749] rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Formulário para novo comentário */}
      {authUser && (
        <div className="flex gap-3">
          <Avatar className="h-8 w-8 border border-[#2A3749]">
            <AvatarImage 
              src={profile?.image_url} 
              alt={profile?.nickname || 'Você'}
              onLoad={() => console.log('✅ [PostComments] Imagem do usuário atual carregada:', profile?.image_url)}
              onError={() => console.log('❌ [PostComments] Erro ao carregar imagem do usuário atual:', profile?.image_url)}
            />
            <AvatarFallback className="bg-[#00B6F0] text-black text-xs">
              {profile?.nickname?.charAt(0).toUpperCase() || 'V'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-2">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Escreva um comentário..."
              className="bg-[#1A2332] border-[#2A3749] text-white placeholder:text-gray-500 resize-none min-h-[80px] focus:ring-[#00B6F0] focus:border-[#00B6F0]"
              maxLength={1000}
              disabled={isCreating}
            />
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">
                {newComment.length}/1000
              </span>
              <Button
                onClick={handleCreateComment}
                disabled={isCreating || !newComment.trim()}
                size="sm"
                className="bg-[#00B6F0] hover:bg-[#00B6F0]/80 text-black font-medium"
              >
                {isCreating ? (
                  <>Publicando...</>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-1" />
                    Comentar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de comentários */}
      <div className="space-y-4">
        {comments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Ainda não há comentários neste post.</p>
            <p className="text-sm">Seja o primeiro a comentar!</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="flex gap-3 group">
              <Avatar className="h-8 w-8 border border-[#2A3749]">
                <AvatarImage 
                  src={comment.author?.avatar_url} 
                  alt={comment.author?.nickname || 'Usuário'}
                  onLoad={() => console.log(`✅ [PostComments] Imagem do comentário ${comment.id} carregada:`, comment.author?.avatar_url)}
                  onError={() => console.log(`❌ [PostComments] Erro ao carregar imagem do comentário ${comment.id}:`, comment.author?.avatar_url)}
                />
                <AvatarFallback className="bg-[#00B6F0] text-black text-xs">
                  {comment.author?.nickname?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1">
                <div className="bg-[#1A2332] border border-[#2A3749] rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="text-white font-medium text-sm">
                        {comment.author?.nickname || 'Usuário Desconhecido'}
                      </span>
                      <span className="text-gray-500 text-xs ml-2">
                        {formatDistanceToNow(new Date(comment.created_at), {
                          addSuffix: true,
                          locale: ptBR
                        })}
                      </span>
                    </div>
                    
                    {/* Menu de ações do comentário */}
                    {authUser?.id === comment.author_id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-gray-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            type="button"
                          >
                            <MoreVertical className="h-3 w-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent 
                          align="end" 
                          className="bg-[#1A2332] border-[#2A3749] z-[9999]"
                        >
                          <DropdownMenuItem
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                            disabled={deletingCommentId === comment.id}
                          >
                            {deletingCommentId === comment.id ? 'Deletando...' : 'Deletar'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                  
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {comment.content}
                  </p>
                </div>
                
                {/* Botão de like do comentário */}
                <div className="flex items-center mt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-500 hover:text-red-400 text-xs h-6 px-2"
                    type="button"
                    onClick={() => {
                      // TODO: Implementar like de comentário
                    }}
                  >
                    <Heart className="h-3 w-3 mr-1" />
                    {comment.likes_count || 0}
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PostComments; 